import React from 'react'
import { Button, TextField } from '@material-ui/core'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import EditIcon from '@material-ui/icons/Edit'

import IconButton from '../../components/IconButton'
import { addCharacter, deleteCharacter, updateCharacter } from '../../api/character'
import { CHAR_CONDITION, CHAR_TYPES } from '../../constants'
import { usePaperExpand } from './util'

const CharacterInput = ({ className, disabled, fullWidth, label, value, onChange, type = 'text' }) => {
  return (
    <TextField
      className={className}
      disabled={disabled}
      fullWidth={fullWidth}
      margin='normal'
      variant='outlined'
      label={label}
      InputLabelProps={{ shrink: true }}
      type={type}
      value={value}
      onChange={({ target }) => {
        onChange(target.value)
      }}
    />
  )
}

const Character = ({ campaignId, characterId, character = {}, isEditing: _isEditing = false, isOnboarding, loading, onCancel, onSuccess, titleClass }) => {
  const [ isEditing, setIsEditing ] = React.useState(_isEditing)
  const [ isSaving, setIsSaving ] = React.useState(false)

  const details = character.details || {}
  const [ name, setName ] = React.useState(details.name || '')
  const [ wounds, setWounds ] = React.useState(details[CHAR_CONDITION.threshold.wounds] || 0)
  const [ strain, setStrain ] = React.useState(details[CHAR_CONDITION.threshold.strain] || 0)
  const [ soak, setSoak ] = React.useState(details.soak || 0)
  const [ credits, setCredits ] = React.useState(details.credits || 0)
  const [ chars, setChars ] = React.useState(details.chars || {})
  const [ totalXP, setTotalXP ] = React.useState(details.totalXP || 0)
  const [ currXP, setCurrXP ] = React.useState(details.currXP || 0)

  // Onboarding should show this open by default
  const { isOpen, expander } = usePaperExpand('character', isOnboarding)

  React.useEffect(() => {
    if (Object.keys(details).length > 0) {
      setName(details.name)
      setWounds(details[CHAR_CONDITION.threshold.wounds])
      setStrain(details[CHAR_CONDITION.threshold.strain])
      setSoak(details.soak)
      setCredits(details.credits)
      setChars(details.chars || {})
    }
  }, [ details ])

  const isDisabled = !isEditing || isSaving

  const onSubmit = async () => {
    setIsSaving(true)

    try {
      const payload = {
        name,
        [CHAR_CONDITION.threshold.wounds]: wounds,
        [CHAR_CONDITION.threshold.strain]: strain,
        soak,
        credits,
        chars,
        currXP,
        totalXP
      }
      if (Object.keys(details).length === 0) {
        await addCharacter(campaignId, payload)
      } else {
        await updateCharacter(campaignId, characterId, payload)
      }
      typeof onSuccess === 'function' && onSuccess()
      setIsEditing(false)
      setIsSaving(false)
    } catch(e) {
      setIsSaving(false)
    }
  }

  const hasCharacter = Object.keys(character).length > 0

  const title = (
    <div className={titleClass} css={`display: flex; margin-bottom: ${!isOpen && '0'};`}>
      {expander}
      Character
    </div>
  )

  if (!isOpen) return title

  return (
    <>
      <div css={`display: flex; justify-content: space-between;`}>
        {title}
        {!loading && !isEditing && hasCharacter &&
          <div css={`display: flex;`}>
            <IconButton onClick={async () => {
              if (window.confirm('Are you sure you want to delete your character?')) {
                setIsSaving(true)
                await deleteCharacter(campaignId, characterId)
                setIsSaving(false)
              }
            }} css={`margin-right: 8px;`}>
              <DeleteOutlineOutlinedIcon />
            </IconButton>
            <IconButton onClick={() => setIsEditing(true)}>
              <EditIcon />
            </IconButton>
          </div>
        }
      </div>
      {loading ?
        <p css={`display: flex; flex: 1; justify-content: center; align-items: center; margin-bottom: 24px;`}>
          Loading...
        </p>
        :
        <>
          <CharacterInput label='Name (required)' disabled={isDisabled} value={name} onChange={setName} fullWidth />
          <div css={`display: flex; justify-content: center; .MuiInputBase-root { max-width: 151px; }`}>
            <CharacterInput type='number' label='Wound Threshold' disabled={isDisabled} value={wounds} onChange={setWounds} />
            <CharacterInput type='number' label='Strain Threshold' disabled={isDisabled} value={strain} onChange={setStrain} css={`margin-left: 8px;`} />
            <CharacterInput type='number' label='Soak' disabled={isDisabled} value={soak} onChange={setSoak} css={`margin-left: 8px;`} />
          </div>
          <div css={`display: flex; justify-content: center; flex-wrap: wrap;`}>
            {Object.keys(CHAR_TYPES).map((label) => {
              const shorthand = CHAR_TYPES[label]

              return (
                <CharacterInput css={`
                    max-width: 82px;
                    margin-right: 8px;
                    label { text-transform: capitalize; }
                    input { font-size: 18px; font-weight: bold; }
                  `}
                  key={label}
                  label={label}
                  disabled={isDisabled}
                  value={chars[shorthand] || ''}
                  onChange={(value) => {
                    setChars({ ...chars, [shorthand]: value })
                  }} />
              )
            })}
          </div>
          <div css={`display: flex; justify-content: center; flex-wrap: wrap;`}>
            <CharacterInput type='number' label='Credits' disabled={isDisabled} value={credits} onChange={setCredits} css={`max-width: 110px; margin-left: 8px;`} />
            <CharacterInput type='number' label='Total XP' disabled={isDisabled} value={totalXP} onChange={setTotalXP} css={`max-width: 82px; margin-left: 8px;`} />
            <CharacterInput type='number' label='Current XP' disabled={isDisabled} value={currXP} onChange={setCurrXP} css={`max-width: 82px; margin-left: 8px;`} />
          </div>
          {isEditing &&
            <div css={`display: flex; justify-content: center; margin-top: 8px;`}>
              <Button onClick={() => {
                setIsEditing(false)
                typeof onCancel === 'function' && onCancel()
              }} css={`margin-right: 8px;`}>
                Cancel
              </Button>
              <Button
                disabled={isSaving || name.length === 0}
                disableElevation
                type='button'
                variant='contained'
                color='primary'
                onClick={onSubmit}
              >
                Save
              </Button>
            </div>
          }
        </>
      }
    </>
  )
}

export default Character
